import React from 'react';
import { Box, Card, Chip, IconButton, Typography } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import { KanbanCardShell } from './KanbanCard';
import { tint } from './tint';
import { COLUMN_WIDTH } from './types';
import type { KanbanColumnModel } from './types';

export interface KanbanColumnProps<TCard> {
  boardScope: string;
  column: KanbanColumnModel;
  /** Already sorted by the board. */
  cards: TCard[];
  getCardId: (card: TCard) => UniqueIdentifier;
  /** Returns the card BODY. The shell (and its drag wiring) is owned here. */
  renderCard: (card: TCard, ctx: { isOverlay: boolean }) => ReactNode;
  getCardActions?: (card: TCard) => ReactNode;
  getCardAccent?: (card: TCard) => string | undefined;
  onCardClick?: (card: TCard) => void;
  boardColor?: string;
  isDropTarget: boolean;
  renderSummary?: (columnId: number, cards: TCard[]) => ReactNode;
  emptyLabel?: string;
  onAddCard?: (columnId: number) => void;
  columnWidth?: number;
}

export function KanbanColumn<TCard>({
  boardScope,
  column,
  cards,
  getCardId,
  renderCard,
  getCardActions,
  getCardAccent,
  onCardClick,
  boardColor,
  isDropTarget,
  renderSummary,
  emptyLabel = 'Nothing here yet',
  onAddCard,
  columnWidth = COLUMN_WIDTH,
}: KanbanColumnProps<TCard>) {
  // The whole column is a drop target — header and padding included — so a
  // drop anywhere sensible lands, not just one directly over a card.
  const { setNodeRef } = useDroppable({
    id: `${boardScope}:col:${column.id}`,
    data: { type: 'column', boardScope, columnId: column.id },
  });

  // `||` not `??`: an unset colour arrives from the API as '', and '' ?? x
  // is '', which would leave every column on a freshly seeded board unaccented.
  const accent = column.color?.trim() || boardColor?.trim() || undefined;
  const overLimit = column.wip_limit != null && cards.length >= column.wip_limit;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: `0 0 ${columnWidth}px`,
        minWidth: columnWidth,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        contain: 'layout style',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          p: 1.25,
          mb: 1,
          flexShrink: 0,
          gap: 0.75,
          borderRadius: 'md',
          ...(accent ? { borderTop: '3px solid', borderTopColor: accent } : {}),
          // A gradient rather than bgcolor: the app's global glass styling
          // overrides background-color on Card, and would swallow a tint.
          ...(accent
            ? { backgroundImage: `linear-gradient(${tint(accent, 0.08)}, ${tint(accent, 0.08)})` }
            : {}),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="title-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {column.name}
          </Typography>
          <Chip size="sm" variant="soft" color={overLimit ? 'warning' : 'neutral'}>
            {column.wip_limit != null ? `${cards.length} / ${column.wip_limit}` : cards.length}
          </Chip>
          {onAddCard && (
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              aria-label={`Add to ${column.name}`}
              onClick={() => onAddCard(column.id)}
            >
              <Plus size={16} />
            </IconButton>
          )}
        </Box>
        {renderSummary?.(column.id, cards)}
      </Card>

      <Box
        role="region"
        aria-label={`${column.name} cards`}
        tabIndex={0}
        sx={{
          flex: 1,
          minHeight: 0,
          // Derived from the bounded wrapper, not the viewport, so the board
          // works inside a panel as well as on a full page.
          maxHeight: '100%',
          overflowY: 'auto',
          overscrollBehaviorY: 'contain',
          scrollbarGutter: 'stable',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 0.5,
          borderRadius: 'md',
          outline: isDropTarget ? '2px dashed' : 'none',
          outlineColor: 'primary.400',
          bgcolor: isDropTarget ? 'primary.softBg' : 'transparent',
          transition: 'background-color 120ms',
        }}
      >
        <SortableContext
          id={String(column.id)}
          items={cards.map(getCardId)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map((card) => (
            <KanbanCardShell
              key={String(getCardId(card))}
              id={getCardId(card)}
              boardScope={boardScope}
              columnId={column.id}
              accentColor={getCardAccent?.(card)}
              actions={getCardActions?.(card)}
              onClick={onCardClick ? () => onCardClick(card) : undefined}
            >
              {renderCard(card, { isOverlay: false })}
            </KanbanCardShell>
          ))}
        </SortableContext>

        {cards.length === 0 && (
          <Typography
            level="body-xs"
            sx={{ color: 'text.tertiary', textAlign: 'center', py: 3, userSelect: 'none' }}
          >
            {emptyLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default KanbanColumn;
